import { useEffect, useState } from 'react';
import { useTranslate } from '@refinedev/core';
import {
  Button,
  Card,
  Divider,
  Flex,
  Segmented,
  Space,
  Tag,
  Typography,
} from 'antd';
import { PauseOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { PhoneFrame } from '@/components/mobile_preview/PhoneFrame';
import { MobilePreviewDisclaimer } from '@/components/mobile_preview/MobilePreviewDisclaimer';
import { MobilePreviewProvider } from '@/components/mobile_preview/MobilePreviewProvider';
import { MobileThemeToggle } from '@/components/mobile_preview/MobileThemeToggle';
import { PreviewAlertList } from '@/components/mobile_preview/PreviewAlertList';
import { BannerPreviewSection } from './BannerPreviewSection';
import {
  AUTOPLAY_DWELL_MS,
  nextPage,
  previewAlerts,
  ratioLabel,
  warningCount,
} from './bannerPreview';
import type { PreviewModel } from './bannerPreview';

const { Text } = Typography;

interface BannerPreviewPanelProps {
  model: PreviewModel;
  selectedPage: number;
  onSelectPage: (page: number) => void;
  /** Creatives still in flight — the model would read as "none" otherwise. */
  loading?: boolean;
}

/** Single-banner framing of BannerPreviewSection, for the banner detail page. */
export const BannerPreviewPanel = ({
  model,
  selectedPage,
  onSelectPage,
  loading = false,
}: BannerPreviewPanelProps) => {
  const t = useTranslate();
  const [autoplay, setAutoplay] = useState(true);

  const count = model.entries.length;
  const running = autoplay && model.isCarousel && !loading;
  const warnings = warningCount(previewAlerts(model));

  useEffect(() => {
    if (!running) return;
    // Re-armed per page, and the successor is derived from the page
    // on screen — a manual jump mid-cycle cannot desync the sequence.
    const timer = setTimeout(
      () => onSelectPage(nextPage(selectedPage, count)),
      AUTOPLAY_DWELL_MS
    );
    return () => clearTimeout(timer);
  }, [running, selectedPage, count, onSelectPage]);

  /** Any deliberate page choice takes the wheel from autoplay. */
  const selectManually = (page: number) => {
    setAutoplay(false);
    onSelectPage(page);
  };

  return (
    <Card
      title={t('user_catalog.banners.preview.title')}
      extra={
        <Space size={8}>
          {model.effectiveSize && (
            <Text type="secondary">
              {t('user_catalog.banners.preview.meta', {
                count,
                ratio: ratioLabel(model.effectiveSize),
              })}
            </Text>
          )}
          {warnings > 0 && (
            <Tag color="gold" style={{ marginInlineEnd: 0 }}>
              {t('user_catalog.banners.preview.warning_count', {
                count: warnings,
              })}
            </Tag>
          )}
        </Space>
      }
    >
      <MobilePreviewProvider>
        <Flex
          justify="center"
          align="center"
          gap={8}
          style={{ marginBottom: 16 }}
        >
          {model.isCarousel && (
            <>
              <Segmented
                value={selectedPage}
                onChange={(value) => selectManually(Number(value))}
                options={model.entries.map((entry) => entry.page)}
                aria-label={t('user_catalog.banners.preview.page_picker')}
              />
              <Button
                size="small"
                icon={autoplay ? <PauseOutlined /> : <PlayCircleOutlined />}
                onClick={() => setAutoplay((on) => !on)}
                aria-pressed={autoplay}
              >
                {t('user_catalog.banners.preview.autoplay')}
              </Button>
            </>
          )}
          <MobileThemeToggle />
        </Flex>

        <PhoneFrame>
          <BannerPreviewSection
            model={model}
            selectedPage={selectedPage}
            onSwipe={selectManually}
            loading={loading}
          />
        </PhoneFrame>

        <MobilePreviewDisclaimer />
      </MobilePreviewProvider>

      <Divider />

      {!loading && (
        <PreviewAlertList
          alerts={previewAlerts(model)}
          keyPrefix="user_catalog.banners.preview.alerts"
        />
      )}
    </Card>
  );
};
